import { useParams, Link } from 'react-router-dom';
import { useMemo } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEOHead from '@/components/feature/SEOHead';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { articles } from '@/mocks/articles';
import { categories } from '@/mocks/categories';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();

  const article = useMemo(
    () => articles.find((a) => a.slug === slug),
    [slug]
  );

  const relatedArticles = useMemo(() => {
    if (!article) return [];
    return articles
      .filter((a) => a.category === article.category && a.slug !== article.slug)
      .slice(0, 3);
  }, [article]);

  const category = useMemo(() => {
    if (!article) return null;
    return categories.find((c) => c.slug === article.category);
  }, [article]);

  if (!article) {
    return (
      <>
        <Navbar />
        <div className="pt-16 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-heading font-bold text-foreground-900 mb-2">文章未找到</h1>
            <p className="text-foreground-600 mb-4">请检查文章地址是否正确</p>
            <Link to="/" className="text-primary-600 hover:text-primary-700 font-medium cursor-pointer">
              返回首页
            </Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    datePublished: article.date,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    publisher: {
      '@type': 'Organization',
      name: '91网页浏览器',
    },
    image: article.image,
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
      />

      <SEOHead
        title={`${article.title} - 91网页浏览器`}
        description={article.excerpt}
        keywords={article.tags.join(',')}
        canonicalUrl={`https://www.91browser.com/article/${article.slug}`}
        ogType="article"
      />

      <Navbar />
      <main className="pt-16">
        <div className="bg-background-100/50">
          <div className="px-4 md:px-6 max-w-6xl mx-auto">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: category?.name || '', href: `/category/${article.category}` },
                { label: article.title },
              ]}
            />
          </div>
        </div>

        <article className="py-12 md:py-16">
          <div className="px-4 md:px-6 max-w-4xl mx-auto">
            <div className="mb-8">
              <div className="flex items-center gap-3 mb-4 flex-wrap">
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-3 py-1 rounded-full bg-secondary-100 text-secondary-700 text-xs font-medium"
                  >
                    {tag}
                  </span>
                ))}
              </div>

              <h1 className="text-2xl md:text-4xl font-heading font-bold text-foreground-950 mb-4 leading-tight">
                {article.title}
              </h1>

              <div className="flex items-center gap-4 text-sm text-foreground-500 flex-wrap">
                <div className="flex items-center gap-2">
                  <div className="w-7 h-7 rounded-full bg-secondary-200 flex items-center justify-center">
                    <span className="text-secondary-700 font-semibold text-xs">{article.author[0]}</span>
                  </div>
                  <span>{article.author}</span>
                </div>
                <span>·</span>
                <span>{article.date}</span>
                <span>·</span>
                <span>{article.readTime}阅读</span>
                {category && (
                  <>
                    <span>·</span>
                    <Link
                      to={`/category/${article.category}`}
                      className="text-primary-600 hover:text-primary-700 font-medium cursor-pointer"
                    >
                      {category.name}
                    </Link>
                  </>
                )}
              </div>
            </div>

            <div className="aspect-[21/9] rounded-xl overflow-hidden mb-10">
              <img
                src={article.image}
                alt={article.title}
                title={`${article.title} - 91网页浏览器`}
                className="w-full h-full object-cover"
              />
            </div>

            <div
              className="prose prose-base max-w-none text-foreground-700 leading-relaxed
                prose-headings:font-heading prose-headings:text-foreground-900 prose-headings:font-semibold
                prose-h2:text-xl prose-h2:mt-10 prose-h2:mb-4
                prose-p:mb-4 prose-p:text-foreground-700 prose-p:leading-relaxed
                prose-strong:text-foreground-900"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            <div className="mt-12 pt-8 border-t border-background-200">
              <div className="flex items-center gap-4">
                <span className="text-sm text-foreground-500">分享文章：</span>
                <a href="#" className="w-9 h-9 rounded-lg bg-background-100 flex items-center justify-center text-foreground-600 hover:bg-primary-100 hover:text-primary-600 transition-colors cursor-pointer" aria-label="分享到微信">
                  <i className="ri-wechat-line w-4 h-4 flex items-center justify-center"></i>
                </a>
                <a href="#" className="w-9 h-9 rounded-lg bg-background-100 flex items-center justify-center text-foreground-600 hover:bg-primary-100 hover:text-primary-600 transition-colors cursor-pointer" aria-label="分享到微博">
                  <i className="ri-weibo-line w-4 h-4 flex items-center justify-center"></i>
                </a>
                <a href="#" className="w-9 h-9 rounded-lg bg-background-100 flex items-center justify-center text-foreground-600 hover:bg-primary-100 hover:text-primary-600 transition-colors cursor-pointer" aria-label="复制链接">
                  <i className="ri-link w-4 h-4 flex items-center justify-center"></i>
                </a>
              </div>
            </div>
          </div>
        </article>

        {relatedArticles.length > 0 && (
          <section className="py-12 md:py-16 bg-background-100/50">
            <div className="px-4 md:px-6 max-w-6xl mx-auto">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-8">
                相关文章
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                {relatedArticles.map((related) => (
                  <Link
                    key={related.slug}
                    to={`/article/${related.slug}`}
                    className="group block rounded-xl overflow-hidden bg-background-50 border border-background-200/60 hover:border-background-300/60 transition-all duration-300 cursor-pointer"
                  >
                    <div className="aspect-[16/10] overflow-hidden">
                      <img
                        src={related.image}
                        alt={related.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-5">
                      <h3 className="font-heading font-semibold text-foreground-900 mb-2 text-sm leading-snug group-hover:text-primary-600 transition-colors line-clamp-2">
                        {related.title}
                      </h3>
                      <div className="flex items-center gap-2 text-xs text-foreground-500">
                        <span>{related.date}</span>
                        <span>·</span>
                        <span>{related.readTime}阅读</span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
      <Footer />
    </>
  );
}